import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  View, StyleSheet, Animated, Text,
} from 'react-native';

import { colors } from '@styleseat/brand';

/**
 * AnimatedCircleCountdown component.
 *
 * This is a basic, animated countdown surrounded by a circle that reflects the
 * number in the counter.
 *
 * @param {number} elapsed the amount to deduct from the counter.
 * @param {number} max the original amount to display on the counter.
 * @param {number} width the width (and height) of the component (also controlls font size).
 * @param {number} strokeWidth the weight of the strokes used on the circle.
 * @param {string} backgroundColor the color used on the back circle and countdown text.
 * @param {string} foregroundColor the color used on the revealed front circle.
 * @param {number} duration  the number of miliseconds for the animation to complete.
 * @param {number} delay the number of miliseconds for the animation to wait before starting.
 */
const AnimatedCircleCountdown = ({
  elapsed, max, width, strokeWidth, backgroundColor, foregroundColor, duration, delay,
}) => {
  const radius = (width - strokeWidth) / 2;
  const circumference = Math.PI * 2 * radius;
  const center = radius + (strokeWidth / 2);
  const [currentText, setCurrentText] = useState(7);
  const [styles, setStyles] = useState(null);
  const [strokeDasharray, setStrokeDasharray] = useState(`0, ${circumference}`);
  const animatedValue = new Animated.Value(0);

  /**
   * @param {number} value the current value of animatedValue.
   * @returns {number} the length of the stroke to use in the foreground.
   */
  const getStrokeLength = value => (circumference / 100) * ((value / max) * 100);

  useEffect(() => {
    setStyles(StyleSheet.create({
      rotated: {
        transform: [
          { rotate: '-90deg' },
        ],
        width,
        height: width,
      },
      wrapper: {
        position: 'relative',
        justifyContent: 'center',
        alignContent: 'center',
        width,
      },
      number: {
        position: 'absolute',
        width: '100%',
        fontSize: Math.floor(width / 2),
        textAlign: 'center',
        color: backgroundColor,
      },
    }));

    // when [animatedValue] changes calculate new [strokeDasharray]
    // for the <circle> in the foreground. This is what causes the SVG animation.
    animatedValue.addListener(animated => {
      setCurrentText(max - Math.round(animated.value));
      setStrokeDasharray(`${getStrokeLength(animated.value)}, ${circumference}`);
    });

    // cause [animatedValue] to count up from 0 to [elapsed]
    // over [duration] ms, and delayed by [delay] ms
    Animated.timing(animatedValue, { toValue: elapsed, duration, delay }).start();
  }, [elapsed, max, width, duration, delay]);

  return (
    <React.Fragment>
      {styles && (
        <View style={styles.wrapper}>
          <View style={styles.rotated}>
            <svg viewBox={`0 0 ${width} ${width}`} width={width} height={width} xmlns="http://www.w3.org/2000/svg">
              <circle stroke={backgroundColor} strokeWidth={strokeWidth} fill="none" cx={center} cy={center} r={radius} />
              <circle
                stroke={foregroundColor}
                strokeWidth={strokeWidth + 1}
                fill="none"
                cx={center}
                cy={center}
                r={radius}
                strokeDasharray={strokeDasharray}
              />
            </svg>
          </View>
          <Text style={styles.number} color={backgroundColor}>{currentText}</Text>
        </View>
      )}
    </React.Fragment>
  );
};

AnimatedCircleCountdown.propTypes = {
  elapsed: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  width: PropTypes.number,
  strokeWidth: PropTypes.number,
  backgroundColor: PropTypes.string,
  foregroundColor: PropTypes.string,
  duration: PropTypes.number,
  delay: PropTypes.number,
};

AnimatedCircleCountdown.defaultProps = {
  strokeWidth: 2,
  backgroundColor: colors.slate,
  foregroundColor: colors.lightGrey,
  width: 52,
  duration: 1000,
  delay: 100,
};

export default AnimatedCircleCountdown;
